import { isUndefined } from '@aloft/toolkit-shared';
import { Flex, Icon, IconButton, Modal, Tooltip, Typography } from '@aloft/toolkit-sui';

import { useState } from 'react';
import { Control, FieldPath, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';

import { selectCompSequence } from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { PropertyGridFieldName, PropertyGridForm, PropertyGridColumnDefinition } from '../types.ts';
import { handleUpdatePropertyData } from '../utils.ts';
import { readOnlyFieldTypographyProps } from './consts.ts';
import { PropertyGridFieldProps } from './types.ts';

const AutofillSubjectValueButton = ({
  fieldName,
  control,
  getValues,
  setValue,
}: {
  fieldName: PropertyGridFieldName;
  control: Control<PropertyGridForm>;
  getValues: UseFormGetValues<PropertyGridForm>;
  setValue: UseFormSetValue<PropertyGridForm>;
}) => {
  const dispatch = useAppDispatch();
  const compSequence = useAppSelector(selectCompSequence);

  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const subjectValue = useWatch({ control, name: `columns.0.propertyData.${fieldName}` as FieldPath<PropertyGridForm> });

  const columns = getValues('columns');

  const compFields = compSequence.map((compId) => ({
    compId,
    columnIndex: columns.findIndex(
      (column: PropertyGridColumnDefinition) => column?.propertyId === compId && column?.type === 'property',
    ),
  })).filter((compField) => compField.columnIndex !== -1);

  const allCompsMatchSubject = compFields.every(
    ({ columnIndex }) =>
      getValues(`columns.${columnIndex}.propertyData.${fieldName}` as FieldPath<PropertyGridForm>) ===
      subjectValue,
  );

  const canAutoFill = compFields.every(({ columnIndex }) => {
    const compValue = getValues(
      `columns.${columnIndex}.propertyData.${fieldName}` as FieldPath<PropertyGridForm>,
    );

    return isUndefined(compValue) || compValue === subjectValue;
  });

  const handleAutofill = () => {
    compSequence.forEach((compId) => {
      const compIndex = columns.findIndex(
        (column: PropertyGridColumnDefinition) => column?.propertyId === compId && column?.type === 'property',
      );

      const propertyData = getValues(`columns.${compIndex}.propertyData`);
      if (propertyData) {
        setValue(`columns.${compIndex}.propertyData.${fieldName}`, subjectValue as any);
        handleUpdatePropertyData(compId, getValues(), dispatch, propertyData);
      }
    });
  };

  const tooltipLabel = 'Paste Subj. Value Across Comps';
  const isDisabled = isUndefined(subjectValue) || allCompsMatchSubject;

  return (
    <>
      <Modal
        size="lg"
        isOpen={isConfirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
        closeOnEsc
        title="Existing Feature Values"
        primaryAction={{
          id: 'override-existing-values-btn',
          text: 'Override',
          onClick: () => {
            handleAutofill();
            setConfirmationModalOpen(false);
          },
        }}
        secondaryAction={{
          text: 'Cancel',
          onClick: () => setConfirmationModalOpen(false),
        }}
      >
        Some comparables already have a value set for this feature. Do you want to overwrite the
        existing values?
      </Modal>
      <Tooltip label={tooltipLabel}>
        <Flex
          paddingRight={isDisabled ? '8px' : '4px'}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
        >
          {isDisabled && <Icon icon="autofill" color="strokeDefault" />}
          {!isDisabled && (
            <IconButton
              variant="tertiary"
              ariaLabel={tooltipLabel}
              icon="autofill"
              iconColor="textPrimary"
              size="xs"
              onClick={() => {
                if (canAutoFill) handleAutofill();
                else setConfirmationModalOpen(true);
              }}
            />
          )}
        </Flex>
      </Tooltip>
    </>
  );
};

export const LabelField = ({
  columnIndex,
  field: { fieldName, label, autofillSubjectValue },
  control,
  getValues,
  setValue,
}: PropertyGridFieldProps) => {
  if (autofillSubjectValue && columnIndex === 0) {
    return (
      <Flex backgroundColor="surface2" height="32px" alignItems="center">
        <Typography {...readOnlyFieldTypographyProps}>{label}</Typography>
        <AutofillSubjectValueButton fieldName={fieldName} control={control} getValues={getValues} setValue={setValue} />
      </Flex>
    );
  }

  return <Typography {...readOnlyFieldTypographyProps}>{label}</Typography>;
};
